import { useTranslation } from 'react-i18next';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { Button, Table, Typography } from 'antd';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { ColumnProps } from 'antd/es/table';
import { UNIT_OPTIONS_VALUES } from 'constants/subscriptions';
import { useUIDispatchContext } from 'providers/UI';
import {
  deleteFeatureItem,
  fetchFeatureItems
} from 'services/api/subscriptions';
import { FeatureItemRow } from 'types/api/subscriptions';

import useOrgEffect from 'hooks/useOrgEffect';
import { Private } from 'components/shared';
import { ActionMenu, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import { useSubscriptionFeatures } from '../hooks';

const PAGE_LIMIT = 10;

type ColumnType = FeatureItemRow;

const { Text } = Typography;

const SubscriptionFeatures = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { toggleModal } = useUIDispatchContext();
  const [results, setResults] = useState<FeatureItemRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchFeatureItems(page, PAGE_LIMIT);
        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey]);

  const features = useSubscriptionFeatures();

  const onDeletePlan = useCallback(
    (id: number) => {
      toggleModal('confirm', true, {
        title: t('subscription.delete_subscription_feature'),
        async onConfirm() {
          try {
            await deleteFeatureItem(id);
            setRefreshKey(k => k + 1);
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    [t, toggleModal]
  );

  const columns: ColumnProps<ColumnType>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: (id: string) => `#${id}`
      },
      {
        title: t('subscription.feature_name'),
        dataIndex: 'subscription_feature_id',
        key: 'subscription_feature_id',
        render: (id: number) => {
          const feature = features?.find(item => item.id === id);

          return (
            <span dangerouslySetInnerHTML={{ __html: feature?.name || '' }} />
          );
        }
      },
      {
        title: t('subscription.measurement_unit'),
        dataIndex: 'unit',
        key: 'unit',
        render: (value: keyof typeof UNIT_OPTIONS_VALUES) => {
          return <Text>{t(`subscription.${UNIT_OPTIONS_VALUES[value]}`)}</Text>;
        }
      },
      {
        title: '',
        dataIndex: 'action',
        key: 'action',
        align: 'right',
        render: (_, { id }) => (
          <ActionMenu
            data={[
              {
                title: t('table.edit'),
                permission: {
                  route: PERMISSION_ROUTES.faq,
                  action: PERMISSION_ACTIONS.update
                },
                action() {
                  navigate(`/subscriptions/features/${id}`);
                },
                menuProps: {
                  style: {
                    color: 'var(--primary)'
                  }
                }
              },
              {
                title: t('table.delete'),
                permission: {
                  route: PERMISSION_ROUTES.faq,
                  action: PERMISSION_ACTIONS.delete
                },
                action() {
                  onDeletePlan(id);
                },
                menuProps: {
                  style: {
                    color: 'var(--danger-color)'
                  }
                }
              }
            ]}
            placement="bottomRight"
            icon={<EllipsisOutlined />}
          />
        )
      }
    ],
    [features, navigate, onDeletePlan, t]
  );

  return (
    <PageWrapper
      color="white"
      title={t('subscription.subscription_features')}
      extra={[
        <Private
          key="add-blog-button"
          route={PERMISSION_ROUTES.blog}
          action={PERMISSION_ACTIONS.create}
        >
          <Button
            type="primary"
            onClick={() => navigate('/subscriptions/features/details')}
          >
            <PlusOutlined />
            {t('subscription.create_feature')}
          </Button>
        </Private>
      ]}
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={results}
        loading={loading}
        pagination={{
          total: total || 0,
          current: page,
          onChange: p => {
            setPage(p);
          },
          showSizeChanger: false
        }}
      />
    </PageWrapper>
  );
};

export default SubscriptionFeatures;
